import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { CallView, DialerQueue, DialerSessionView, DialerSkipReason, DialerSource, DialerSummary, LeadCardResponse, PhoneSlot } from "@gioia365/contracts";
// Soltanto il sottopercorso: l'indice del dominio porta nel bundle il dataset dei comuni.
import { initialDialerState, isAdvancing, reduceDialerState, type DialerBrowserEvent, type DialerBrowserState } from "@gioia365/domain/dialer-session";
import { useInfiniteQuery, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ApiRequestError } from "../api/http";
import { useToast } from "../design/components/Toast";
import { messageForError } from "../i18n/errors";
import { useT } from "../i18n/locale";
import { leadCardQuery, timelineQuery } from "../leads/lead-queries";
import { useOptionalAccess, useOptionalTab } from "../session/access";
import { tabKey, type TabContext, type TabKey } from "../session/tab-context";
import { leadCardSearchOf } from "../tasks/task-search";
import { isCallLive, numberDiagnosisOf, useCallSession, usePreloadBrowserPhone, type CallSession, type CallSessionApi } from "../telephony/call-session";
import {
  currentDialerQuery,
  dialerKeys,
  dialerQueueQuery,
  requestDialerAdvance,
  requestDialerHeartbeat,
  requestDialerInterrupt,
  requestDialerSession,
  requestDialerSkip,
  requestDialerStart,
  requestDialerSummary,
  requestDialerSwitch,
} from "./dialer-queries";

// La sessione del Power Dialer nel browser (piano M5, Task 5; D3–D6): `DialerSessionProvider` vive nel guscio, dentro
// `CallSessionProvider` di M4, quindi cambiare pagina non la ferma. Il server è la verità della sessione (D3: il guscio tiene
// soltanto ciò che il server ha risposto, nella cache della scheda): all'avvio del guscio la legge con `GET
// /api/dialer-sessions/current`, poi la aggiornano le risposte dei comandi. Il browser tiene soltanto la fase del Lead aperto
// (`reduceDialerState` del dominio) e il conto alla rovescia, con i secondi della vista che ha aperto quel Lead (la regola
// dell'azienda letta a ogni vista, ruling Q45).
//
// - Il conto alla rovescia scorre soltanto sulla scheda del Lead aperto (la registra `LeadCardPage` con `useDialerCard`),
//   senza finestre aperte né comandi in corso: fuori dalla scheda la barra dice «Sessione in corso su …» con «Torna alla
//   scheda», e la chiamata non parte mentre si guarda altro. A zero (o con «Chiama subito») la chiamata parte con
//   `useCallSession().start`, sul primo telefono che si chiama nell'ordine della scheda (ruling Q7), con `dialerSessionId`.
// - La sessione ritrovata (un ricaricamento, un'altra scheda che ha fatto un passo) non chiama da sola: il conto alla rovescia
//   resta fermo finché non si preme «Chiama subito», «Riprendi» o «Lead successivo» (mai una seconda chiamata al ricaricamento),
//   e non passa da sola al Lead successivo. Se il Lead aperto è già stato chiamato in questa sessione (`currentCalled` della
//   vista: una chiamata ancora aperta o arrivata al Lead, per esempio un ricaricamento dopo l'esito) non ha un conto alla
//   rovescia da riprendere: riparte da «fatto», con «Lead successivo» e senza «Chiama subito» (prova del 6 ottobre 2026, in cui
//   un clic su «Chiama subito» richiamava lo stesso Lead). Dopo una chiamata mai partita (il telefono del browser che non si
//   carica, il microfono negato: i rimedi chiedono proprio di ricaricare) `currentCalled` è falso e il Lead riparte dal conto
//   fermo con «Chiama subito», come nella pagina aperta (ruling Q20).
// - Le fasi della postazione di M4 portano la fase del Lead: la chiamata partita, finita, l'esito salvato con i passi fatti
//   (`stepsDone`, ruling Q8: è ciò che accende «Lead successivo»), la postazione chiusa. Una chiamata che non parte o non
//   arriva al Lead (microfono, rifiuto del server, abbandono, `not_connected`) torna al conto alla rovescia in pausa, mai ferma
//   in «chiamata» (ruling Q20); la sessione si rilegge, così un Lead preso intanto da un collega si salta da solo.
// - Dopo la chiamata il Lead successivo arriva da solo (decisione del product owner del 9 ottobre 2026): con la postazione chiusa
//   da «Chiudi la chiamata» parte il conto verso il successivo, con i secondi della vista (`advanceSeconds`, la regola
//   dell'azienda letta a ogni vista come i secondi prima della chiamata), che scorre alle stesse condizioni del conto alla
//   rovescia e in più aspetta le finestre della scheda, la scrittura e la scheda del browser nascosta (`pageHoldsAdvance`); a
//   zero si avanza con il comando di «Lead successivo», una volta sola. Con un tentativo della sequenza a metà il
//   passaggio lo chiude (`passOver`, come «Apri il Lead successivo» della finestra «Decidi tu»: il Commerciale non è obbligato
//   alle chiamate di fila). Il Lead che si salta da solo segue lo stesso conto. Il conto non parte con l'esito salvato a
//   postazione aperta (chi scrive una nota non viene portato via), dopo «Richiama ora» (la postazione non si chiude) né per la
//   sessione ritrovata; «Resta su questo Lead» (`stay`) lo toglie, «Pausa» lo ferma.
// - Senza risposta il Lead si richiama da solo (decisione del product owner dell'11 ottobre 2026, TEL-32): con «Senza risposta,
//   richiama da solo…» acceso (`autoRedial` della vista) e la sessione aperta da questa pagina, una chiamata con la sessione che il
//   telefono del browser chiude senza che nessuno abbia risposto (`unanswered`) e senza errori del telefono non chiede l'esito: la
//   postazione aspetta quello di Gioia365 (`awaitingOutcome`, deciso nello stesso disegno della fase dell'esito, così il passo
//   dell'esito non compare nemmeno per un attimo) rileggendo la vista ogni secondo, al più 15 volte, e lo prende senza mandarlo
//   (`acceptAutomaticOutcome`, soltanto per la chiamata che aspetta), anche se intanto l'impostazione è stata spenta o la sessione
//   non è più viva (l'esito è già sul server; il richiamo, invece, soltanto con l'impostazione accesa). L'attesa non blocca
//   (ruling S3): «Scegli l'esito» la chiude subito, e dopo non si prende più l'esito di Gioia365 per quella chiamata; «Interrompi»
//   resta acceso, chiude l'attesa allo stesso modo e poi segue le sue regole (prima l'esito); la chiamata finita senza quell'esito,
//   l'impostazione spenta intanto o 15 secondi di letture senza esito (anche se una lettura non torna) riportano il passo
//   dell'esito di sempre. Chiusa la postazione con «Nessuna risposta» (di Gioia365, o scelta a mano con «Chiudi la chiamata»; non
//   un esito diverso arrivato tardi dopo quello di Gioia365, che resta la scelta di chi chiama) il Lead è subito «fatto», e la vista
//   riletta decide: con il tentativo della sequenza a metà il conto del richiamo sullo stesso Lead (`redial`), che scorre come il
//   conto alla rovescia e aspetta la pagina come il conto verso il Lead successivo (senza secondi, la chiamata aspetta la scheda e
//   la pagina prima di partire); altrimenti il conto verso il Lead successivo. Una rilettura fallita, o che torna dopo 15 secondi,
//   lascia il Lead «fatto» senza nessun conto (ruling 7 del controllore), con «Lead successivo» a mano. Un comando di chi chiama
//   intanto («Lead successivo», «Interrompi», «Chiama») annulla la decisione. La sessione ritrovata, e una chiamata ripresa con
//   «Manca l'esito…», non richiamano mai da sole; «Salta» resta spento finché la vista dice che il Lead aperto è già stato
//   chiamato (`currentCalled`).
// - «Pausa» su un Lead già fatto o che si salta da solo (anche dopo un ricaricamento) non spegne «Lead successivo» (product owner,
//   9 ottobre 2026; prova dello stesso giorno, in cui servivano due clic e la postazione diceva «Fatto tutto»): il clic riprende
//   la sessione sul server e avanza con la versione ripresa, in un solo comando (`advanceWith` con `resumeFirst`); il Lead dopo
//   si apre «fresco», con la sessione attiva e il suo conto alla rovescia. Il conto verso il Lead successivo che scade da solo
//   non riprende mai una pausa. Dal conto alla rovescia, dalla chiamata o dall'esito «Lead successivo» resta spento: si riprende prima.
// - Ogni passo (avvio, «Lead successivo», «Salta», «Continua a chiamare») apre la scheda del Lead dopo, già letta: la vista ne
//   dice il prossimo (`next`) e il guscio ne tiene lette scheda e Timeline (`WarmNextLead`), anche dopo una chiamata lunga.
//   Una sessione attiva senza Lead aperto (un collega teneva il candidato in quell'istante) avanza di nuovo da sola dopo un
//   attimo; l'avvio con `409 dialer_queue_busy` si riprova con attese brevi; dopo i tentativi, il messaggio semplice (ruling
//   Q28).
// - Il battito parte ogni 60 secondi finché la sessione è viva. Una sessione interrotta o finita altrove (il giro del server
//   dopo `heartbeat_lost`) si rilegge e si mostra il suo riepilogo, mai un errore ripetuto.
// - Nella «Guarda come…», nel contesto di piattaforma e senza la funzione `call` non c'è niente: nessuna lettura, nessuna
//   barra (ruling Q27). `beforeunload` resta quello di M4, soltanto durante una chiamata.

/** Il battito del browser (D4): il giro del server interrompe una sessione senza battito da più di due minuti. */
const HEARTBEAT_MS = 60_000;
const TICK_MS = 1_000;
/** Le attese prima di riprovare l'avvio con la coda occupata (ruling Q28): tre tentativi in più, poi il messaggio. */
const START_RETRY_MS = [500, 1_000, 2_000] as const;
/** Una sessione attiva senza Lead aperto avanza di nuovo dopo un attimo, al più cinque volte di fila (ruling Q28). */
const BUSY_ADVANCE_MS = 1_000;
const BUSY_ADVANCE_TRIES = 5;
/** «Lead successivo» ha la sua chiave: un guasto della rete si riprova con la stessa, due volte. */
const NETWORK_RETRIES = 2;
/** Dopo una chiamata senza risposta, la vista si rilegge ogni secondo, al più 15 volte, aspettando l'esito di Gioia365 (TEL-32). */
const AUTOMATIC_OUTCOME_READ_MS = 1_000;
const AUTOMATIC_OUTCOME_READS = 15;
/**
 * La rilettura che decide fra il richiamo e il conto verso il Lead successivo ha lo stesso limite dell'attesa: una risposta più
 * tarda vale come una lettura fallita, così una rete ferma non fa partire una chiamata vera minuti dopo, a sorpresa (TEL-32).
 */
const DECISION_READ_MS = AUTOMATIC_OUTCOME_READS * AUTOMATIC_OUTCOME_READ_MS;

/** La scheda del Lead che la pagina mostra: ciò che serve per chiamarlo (il telefono e il nome della barra di M4). */
export type DialerCardTarget = {
  tab: string;
  leadId: string;
  phone: PhoneSlot | null;
  leadName: string;
};

/** Il riepilogo da mostrare dopo «Interrompi», la fine della lista o una sessione interrotta altrove. */
export type DialerSummaryShown = {
  summary: DialerSummary;
  source: DialerSource;
  sourceName: string;
  tab: TabContext;
};

export type DialerSessionContext = {
  session: DialerSessionView | null;
  browser: DialerBrowserState | null;
  start(input: { tab: TabContext; source: DialerSource; mode: "start" | "resume" | "restart" }): Promise<void>;
  callNow(): void;
  pause(): Promise<void>;
  resume(): Promise<void>;
  /** «Resta su questo Lead»: il conto verso il Lead successivo si toglie, si passa a mano con «Lead successivo». */
  stay(): void;
  /**
   * `false` quando il salto è rifiutato e il rifiuto si dice (`notice`): il Lead resta da saltare, la finestra resta aperta e il
   * conto alla rovescia fermo. `true` altrimenti (saltato, oppure la sessione cambiata altrove e riletta).
   */
  skip(input: { reason: DialerSkipReason; note: string | null }): Promise<boolean>;
  next(input: { passOver: boolean }): Promise<void>;
  interrupt(): Promise<DialerSummary>;
  queue(): UseQueryResult<DialerQueue>;
  /** La scheda di questa sessione (`null` dove il Power Dialer non c'è). */
  tab: TabContext | null;
  /** Il fuso dell'azienda di questa sessione, per le ore che i testi dicono (il richiamo concordato di un Lead); `null` dove il Power Dialer non c'è. */
  timeZone: string | null;
  /** La sessione ritrovata: il conto alla rovescia è fermo finché non si chiama, si riprende o si avanza. */
  restored: boolean;
  /** Un comando in corso: i comandi si spengono, il conto alla rovescia (e quello verso il Lead successivo) si ferma. */
  busy: boolean;
  /** Perché «Interrompi» aspetta (ruling Q46): la chiamata del Lead aperto in corso, oppure l'esito e i passi da fare. */
  interruptBlocked: DeskBlock | null;
  /** La chiamata di cui la postazione aspetta l'esito «Nessuna risposta» di Gioia365 (TEL-32), `null` se nessuna. */
  awaitingOutcome: string | null;
  /** «Scegli l'esito» (ruling S3): l'attesa dell'esito di Gioia365 per quella chiamata finisce subito, e l'esito lo dà chi chiama. */
  chooseOutcome(callId: string): void;
  /** L'ultimo rifiuto di un comando, da dire nella barra. */
  notice: unknown;
  summary: DialerSummaryShown | null;
  closeSummary(): void;
  /** Ferma il conto alla rovescia finché la funzione restituita non si chiama (una finestra aperta). */
  hold(): () => void;
  /** Accende la lettura della coda (la finestra «Coda» aperta). */
  watchQueue(open: boolean): void;
  registerCard(card: DialerCardTarget): () => void;
};

const DialerContext = createContext<DialerSessionContext | null>(null);

/** La sessione del Power Dialer del guscio; soltanto dentro `DialerSessionProvider`. */
export function useDialerSession(): DialerSessionContext {
  const dialer = useContext(DialerContext);
  if (!dialer) throw new Error("useDialerSession outside DialerSessionProvider");
  return dialer;
}

/** La stessa, `null` fuori dal provider: per i componenti di M4 che vivono anche senza. */
export function useOptionalDialerSession(): DialerSessionContext | null {
  return useContext(DialerContext);
}

/** La sessione attiva il cui Lead aperto è questo: «Chiama» della scheda chiama con lei (D5). */
export function useDialerSessionIdFor(tab: TabContext, leadId: string): string | undefined {
  const dialer = useContext(DialerContext);
  const session = dialer?.session;
  if (!dialer?.tab || !session || session.state !== "active" || session.current?.id !== leadId || tabKey(dialer.tab) !== tabKey(tab)) return undefined;
  return session.id;
}

/** Il primo telefono che si chiama, nell'ordine della scheda (quello di «Chiama», ruling Q7); `null` se nessuno. */
export function firstCallablePhone(card: LeadCardResponse): PhoneSlot | null {
  if (!card.permissions.canCall || !card.calling.callable) return null;
  for (const rule of card.calling.phones) {
    if (!rule.callable) continue;
    const phone = card.lead.phones.find((candidate) => candidate.id === rule.phoneId);
    if (phone) return { holder: phone.holder, kind: phone.kind };
  }
  return null;
}

/**
 * La scheda del Lead registrata per la cascata (piano M5, Task 5): finché la pagina la mostra, la chiamata del Lead aperto
 * parte dal provider con il suo telefono e il suo nome, e il conto alla rovescia scorre.
 */
export function useDialerCard(tab: TabContext, card: LeadCardResponse | null, leadName: string | null): void {
  const register = useContext(DialerContext)?.registerCard;
  const key = tabKey(tab);
  const leadId = card?.lead.id ?? null;
  const phone = card ? firstCallablePhone(card) : null;
  const holder = phone?.holder ?? null;
  const kind = phone?.kind ?? null;
  useEffect(() => {
    if (!register || leadId === null || leadName === null) return;
    return register({
      tab: key,
      leadId,
      phone: holder !== null && kind !== null ? { holder, kind } : null,
      leadName,
    });
  }, [register, key, leadId, holder, kind, leadName]);
}

/** Una chiave di idempotenza del browser. */
function newRequestId(): string {
  return crypto.randomUUID();
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
const isCode = (error: unknown, ...codes: string[]): error is ApiRequestError => error instanceof ApiRequestError && codes.includes(error.code);
/** I rifiuti di una sessione cambiata altrove: si rilegge e la barra la mostra com'è. */
const rereads = (error: unknown): boolean => isCode(error, "version_conflict", "dialer_session_paused", "invalid_state", "dialer_session_not_found");
const isLive = (view: DialerSessionView) => view.state === "active" || view.state === "paused";
const leadKey = (view: DialerSessionView) => (view.current ? `${view.id}:${view.current.id}` : null);

/** La fase del Lead aperto, con ciò che serve al browser per lui. */
type Machine = {
  key: string;
  state: DialerBrowserState;
  restored: boolean;
  dialPending: boolean;
};

/**
 * Il Lead appena aperto: con la vista di un comando di questa pagina (`fresh`) il conto alla rovescia parte; ritrovato no,
 * resta fermo (anche con «niente, chiama subito»: un secondo, fermo). Ritrovato su un Lead già chiamato in questa sessione
 * (`currentCalled`: una chiamata ancora aperta o arrivata al Lead, mai una abbandonata prima di comporre) riparte da «fatto»,
 * senza conto verso il Lead successivo. Una sessione in pausa apre il Lead in pausa.
 */
function machineOf(view: DialerSessionView, key: string, fresh: boolean): Machine {
  let state = initialDialerState(view.countdownSeconds, view.current?.passover != null, view.advanceSeconds);
  let dialPending = false;
  if (!fresh && view.currentCalled) {
    // Ritrovata su un Lead già chiamato in questa sessione (un ricaricamento dopo l'esito): riparte da «fatto», con «Lead
    // successivo» e senza «Chiama subito», e non passa da solo al Lead dopo. Una chiamata del Lead ancora senza esito la riprende
    // la postazione (`resume` di M4), e le sue fasi riportano la cascata a «esito» e poi a «fatto».
    state = { phase: "done", secondsLeft: 0, pausedFrom: null, advanceLeft: null, redial: null };
  } else if (state.phase === "calling") {
    if (fresh) dialPending = true;
    else state = { phase: "countdown", secondsLeft: 1, pausedFrom: null, advanceLeft: null, redial: null };
  }
  if (view.state === "paused") state = reduceDialerState(state, { type: "pause" });
  return { key, state, restored: !fresh, dialPending };
}

/** I campi `input` in cui non si scrive: un clic su una scelta non è scrittura. */
const NOT_TYPED = new Set(["button", "checkbox", "radio", "submit", "reset", "image", "file", "range", "color", "hidden"]);

/**
 * Chi lavora ancora sulla scheda (controllo del Task 6, 9 ottobre 2026): una finestra aperta nella pagina (una nota, la modifica
 * del Lead, il WhatsApp, il richiamo) o il cursore in un campo in cui si scrive. Finché c'è, il conto verso il Lead successivo
 * non scorre e non avanza: porterebbe via la persona sotto la finestra o a metà di una parola. Le finestre della barra fermano
 * già ogni conto con `hold()`; quelle della scheda no, e si guardano nella pagina a ogni secondo. Lo stesso con la scheda del
 * browser nascosta (un'altra scheda, un'altra app; controllo finale, 9 ottobre 2026): il Lead dopo arriverebbe, e il suo conto
 * alla rovescia lo chiamerebbe, senza nessuno davanti allo schermo; tornati sulla scheda, il conto riprende dai secondi rimasti.
 * Il conto alla rovescia prima della chiamata non guarda niente di questo.
 */
function pageHoldsAdvance(): boolean {
  if (document.visibilityState === "hidden") return true;
  if (document.querySelector('dialog[open], [role="dialog"], [role="alertdialog"], [aria-modal="true"]') !== null) return true;
  const focused = document.activeElement;
  if (!(focused instanceof HTMLElement)) return false;
  if (focused.isContentEditable || focused.closest('[contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"]') !== null) return true;
  if (focused instanceof HTMLTextAreaElement || focused instanceof HTMLSelectElement) return true;
  return focused instanceof HTMLInputElement && !NOT_TYPED.has(focused.type);
}

/** La chiamata di M4 è del Lead aperto, nella scheda della sessione. */
function callOfLead(session: CallSession, tab: string, leadId: string): boolean {
  return session.phase !== "idle" && session.target !== null && session.target.leadId === leadId && tabKey(session.target.tab) === tab;
}

/** Ciò che tiene la sessione sul Lead aperto: la chiamata in corso (o appena chiesta), oppure l'esito e i passi da fare. */
export type DeskBlock = "call" | "outcome";

/**
 * La postazione del Lead aperto ancora da finire (ruling Q46, come Q8 per «Lead successivo»): la chiamata partita o che sta
 * per partire, oppure la postazione con l'esito o i passi obbligatori ancora da fare. `null` quando la sessione si può fermare.
 */
function deskBlock(phone: CallSessionApi, tab: string | null, leadId: string | null, phase: DialerBrowserState["phase"] | null): DeskBlock | null {
  if (phase === "calling") return "call";
  if (tab === null || leadId === null || !callOfLead(phone.session, tab, leadId)) return null;
  if (isCallLive(phone.session) || phone.session.phase === "ending") return "call";
  return phone.session.phase === "outcome" && !phone.stepsDone ? "outcome" : null;
}

/**
 * La chiamata di cui la postazione aspetta l'esito «Nessuna risposta» di Gioia365 (TEL-32), `null` se nessuna: la chiamata con
 * questa sessione al Lead aperto, chiusa dal telefono del browser senza che nessuno abbia risposto (`unanswered`), senza un errore
 * del telefono (`notice`: non è una chiamata senza risposta, e l'esito si sceglie subito) e con l'esito ancora da dare, nella
 * sessione viva aperta da questa pagina (non ritrovata) con «Senza risposta, richiama da solo…» acceso. Si calcola nel disegno
 * stesso, non dopo un effetto: il passo dell'esito non compare nemmeno per un attimo. `released` è la chiamata la cui attesa è
 * finita («Scegli l'esito», 15 letture, la chiamata finita senza l'esito di Gioia365, l'impostazione spenta): non si aspetta più.
 */
function awaitedCall(phone: CallSession, view: DialerSessionView | null, tab: string | null, machine: Machine | null, released: string | null): string | null {
  if (phone.phase !== "outcome" || !phone.unanswered || phone.notice !== null || phone.save.state !== "none" || phone.call.id === released) return null;
  // La diagnosi del numero di una chiamata che non ha contato (TEL-10) non riceve l'esito di Gioia365: si sceglie, o si rimanda.
  if (numberDiagnosisOf(phone.call) !== null) return null;
  if (view === null || !isLive(view) || !view.autoRedial || view.current === null || machine === null || machine.restored) return null;
  if (tab === null || phone.target.dialerSessionId !== view.id || !callOfLead(phone, tab, view.current.id)) return null;
  return phone.call.id;
}

/** La risposta di `promise`, oppure `null` se non arriva entro `ms` (o se rifiuta). */
function answerWithin<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);
    const settle = (value: T | null) => {
      clearTimeout(timer);
      resolve(value);
    };
    promise.then(settle, () => settle(null));
  });
}

/**
 * Aspetta, guardando ogni secondo, che `held` non trattenga più il passo (la scheda del browser nascosta, una finestra aperta, il
 * cursore in un campo, la scheda del Lead non nella pagina): `false` se intanto `still` non vale più (la decisione annullata, un
 * altro Lead aperto).
 */
async function untilFree(held: () => boolean, still: () => boolean): Promise<boolean> {
  while (held()) {
    await wait(TICK_MS);
    if (!still()) return false;
  }
  return true;
}

export function DialerSessionProvider({ children }: { children: ReactNode }) {
  const tab = useOptionalTab();
  const access = useOptionalAccess();
  // Chi chiama, nella sua azienda: mai nella «Guarda come…» né nel contesto di piattaforma (ruling Q27), mai senza `call`.
  const scope =
    tab !== null && tab.kind === "membership" && tab.commercialView === undefined && access !== null && !access.isPlatform && access.can("call") ? tab : null;
  const value = useDialerState(scope, scope ? (access?.context.settings.timeZone ?? null) : null);
  // Il telefono del browser si scarica in sottofondo per chi chiama (stessa condizione): la prima chiamata non lo aspetta.
  usePreloadBrowserPhone(scope !== null);
  const nextLead = value.session?.next?.id ?? null;
  return (
    <DialerContext.Provider value={value}>
      {value.tab && nextLead !== null ? <WarmNextLead tab={value.tab} leadId={nextLead} /> : null}
      {children}
    </DialerContext.Provider>
  );
}

/**
 * Il Lead dopo, letto in anticipo finché la vista lo dice (D5): la scheda e la prima pagina della Timeline, con le chiavi di
 * `LeadCardPage`, così «Lead successivo» le mostra subito. Un osservatore e non una lettura sola: una lettura che nessuno
 * guarda la cache la butta dopo cinque minuti, cioè durante una chiamata lunga; osservata resta (e si rilegge come le altre).
 */
function WarmNextLead({ tab, leadId }: { tab: TabContext; leadId: string }) {
  useQuery(leadCardQuery(tab, leadId, null));
  useInfiniteQuery(timelineQuery(tab, leadId, "all", "", null));
  return null;
}

function useDialerState(tab: TabContext | null, timeZone: string | null): DialerSessionContext {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const calls = useCallSession();
  const scopeKey: TabKey | null = tab ? tabKey(tab) : null;

  const current = useQuery(currentDialerQuery(tab));
  const session = tab ? (current.data ?? null) : null;
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<unknown>(null);
  const [summary, setSummary] = useState<DialerSummaryShown | null>(null);
  const [holds, setHolds] = useState(0);
  const [queueOpen, setQueueOpen] = useState(false);
  const [card, setCard] = useState<DialerCardTarget | null>(null);
  /** La fase del Lead aperto, con la sua chiave (sessione e Lead), dopo il primo evento. */
  const [stored, setStored] = useState<Machine | null>(null);
  /** L'ultimo Lead aperto da un comando di questa pagina: soltanto per lui il conto alla rovescia parte da solo. */
  const [freshKey, setFreshKey] = useState<string | null>(null);
  /** La chiamata la cui attesa dell'esito di Gioia365 è finita (TEL-32): la postazione chiede l'esito come sempre. */
  const [released, setReleased] = useState<string | null>(null);

  // La fase del Lead aperto segue la vista: un Lead nuovo (o un'altra sessione) riparte da capo.
  const key = session && isLive(session) ? leadKey(session) : null;
  const initial = useMemo(() => (key !== null && session ? machineOf(session, key, freshKey === key) : null), [key, freshKey, session]);
  const machine = key !== null && stored?.key === key ? stored : initial;
  const browser = machine?.state ?? null;

  // Le copie di adesso, per le funzioni che continuano dopo un `await` o che partono da un timer.
  const tabRef = useRef(tab);
  const sessionRef = useRef(session);
  const callsRef = useRef(calls);
  const cardRef = useRef(card);
  const machineRef = useRef(machine);
  const busyRef = useRef(false);
  /** Come `awaiting`, per le letture che arrivano dopo un `await`. */
  const awaitingRef = useRef<string | null>(null);
  /** Come `released`, subito: una lettura che arriva dopo «Scegli l'esito» non prende più l'esito di Gioia365. */
  const releasedRef = useRef<string | null>(null);
  /**
   * Le letture dell'attesa dell'esito di Gioia365 della chiamata `callId`, una alla volta: `asked` conta i secondi passati, anche
   * quelli in cui la lettura di prima non era ancora tornata, così una rete lenta non allunga l'attesa oltre le 15 letture chieste.
   */
  const readsRef = useRef<{ callId: string; asked: number; reading: boolean } | null>(null);
  /** La chiamata la cui postazione si è chiusa con l'esito di Gioia365: la chiusura decide il richiamo, non il conto di TEL-27. */
  const automaticClose = useRef<string | null>(null);
  /** La decisione dopo «Nessuna risposta» in corso (TEL-32): un comando di chi chiama intanto la annulla. */
  const deciding = useRef<object | null>(null);
  const busyTries = useRef(0);
  const busyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Se la scheda del browser è ancora nell'azienda (e nella vista) della richiesta `at`: una risposta arrivata dopo un cambio di
   * azienda non si applica e non apre schede, come in `start()` (rilievo di Codex sulla #94, turno di notte del 10 ottobre 2026).
   */
  const stillOn = useCallback((at: TabContext) => tabRef.current !== null && tabKey(tabRef.current) === tabKey(at), []);
  useLayoutEffect(() => {
    tabRef.current = tab;
    sessionRef.current = session;
    callsRef.current = calls;
    cardRef.current = card;
    machineRef.current = machine;
  });

  /** La fase nuova del Lead aperto: subito per gli eventi che seguono, poi per il disegno. */
  const commit = useCallback((next: Machine) => {
    machineRef.current = next;
    setStored(next);
  }, []);

  const setView = useCallback(
    (view: DialerSessionView | null) => {
      if (scopeKey === null) return;
      queryClient.setQueryData<DialerSessionView | null>(dialerKeys.current(scopeKey), (old) =>
        view && old && old.id === view.id && old.version > view.version ? old : view,
      );
    },
    [scopeKey, queryClient],
  );

  const dispatch = useCallback(
    (event: DialerBrowserEvent) => {
      const before = machineRef.current;
      if (!before) return;
      const state = reduceDialerState(before.state, event);
      // Il conto alla rovescia arrivato a zero, «Chiama subito», o un richiamo senza secondi (TEL-32): la chiamata è da far partire.
      const startsCall =
        state.phase === "calling" && ((before.state.phase === "countdown" && (event.type === "tick" || event.type === "call_now")) || event.type === "redial");
      // Chi chiama ha deciso lui («Lead successivo», «Interrompi», «Resta su questo Lead», una chiamata): la decisione dopo
      // «Nessuna risposta» ancora in corso non vale più (TEL-32).
      if (event.type === "stay" || event.type === "call_now" || event.type === "call_started") deciding.current = null;
      const restored = event.type === "call_started" || event.type === "call_now" || event.type === "resume" ? false : before.restored;
      let dialPending = before.dialPending;
      // La chiamata da fare resta sotto una pausa presa da «chiamata in partenza» (la scheda ancora in arrivo): «Riprendi» la fa
      // partire (rilievo di Codex sulla #94). `dial` chiama soltanto nella fase `calling`.
      const pausedBeforeDialing = state.phase === "paused" && state.pausedFrom === "calling";
      if (startsCall) dialPending = true;
      else if (event.type === "call_started" || (state.phase !== "calling" && !pausedBeforeDialing)) dialPending = false;
      if (state === before.state && restored === before.restored && dialPending === before.dialPending) return;
      commit({ ...before, state, restored, dialPending });
    },
    [commit],
  );

  const showSummary = useCallback(
    async (view: DialerSessionView) => {
      const at = tabRef.current;
      if (!at) return;
      try {
        const shown = await requestDialerSummary(at, view.id);
        if (!stillOn(at)) return;
        setSummary({
          summary: shown,
          source: view.source,
          sourceName: view.sourceName,
          tab: at,
        });
      } catch (error) {
        toast.show(messageForError(t, error), "warning");
      }
    },
    [stillOn, t, toast],
  );

  const goTo = useCallback(
    (view: DialerSessionView) => {
      if (isLive(view) && view.current)
        void navigate({
          to: "/lead/$leadId",
          params: { leadId: view.current.id },
          // Dai Task la scheda sa da dove arriva: «Torna ai Task» alla stessa scheda e persona (piano M5, Task 21).
          search: leadCardSearchOf(view.source),
        });
    },
    [navigate],
  );

  // Il riferimento a `autoAdvance`, che `applyView` programma e che a sua volta usa `applyView`.
  const autoAdvanceRef = useRef<() => void>(() => {});

  /**
   * La vista arrivata dal server: viva, diventa la sessione del guscio (mai una più vecchia di quella che c'è); interrotta o
   * finita, la sessione lascia il guscio e si mostra il suo riepilogo. Attiva senza Lead aperto, si avanza di nuovo fra poco.
   */
  const applyView = useCallback(
    (view: DialerSessionView, options: { fresh?: boolean } = {}) => {
      if (!isLive(view)) {
        setView(null);
        void showSummary(view);
        return;
      }
      const opened = leadKey(view);
      if (options.fresh && opened) {
        setFreshKey(opened);
        // Lo stesso Lead riaperto da un comando («Continua a chiamare» dopo «Interrompi»): la sua fase riparte da capo.
        setStored((before) => (before?.key === opened ? null : before));
      }
      setView(view);
      if (view.state === "active" && view.current === null) {
        if (busyTimer.current) clearTimeout(busyTimer.current);
        if (busyTries.current < BUSY_ADVANCE_TRIES) {
          busyTries.current += 1;
          busyTimer.current = setTimeout(() => autoAdvanceRef.current(), BUSY_ADVANCE_MS);
        } else {
          setNotice(new ApiRequestError(409, "dialer_queue_busy"));
        }
      } else if (view.current) {
        busyTries.current = 0;
      }
    },
    [setView, showSummary],
  );

  /** La sessione riletta dal server; sparita (`404`), lascia il guscio con il motivo. */
  const reread = useCallback(async (): Promise<DialerSessionView | null> => {
    const at = tabRef.current;
    const now = sessionRef.current;
    if (!at || !now) return null;
    try {
      const view = await requestDialerSession(at, now.id);
      if (!stillOn(at)) return null;
      applyView(view);
      return view;
    } catch (error) {
      if (!stillOn(at)) return null;
      if (isCode(error, "dialer_session_not_found")) {
        setView(null);
        toast.show(messageForError(t, error), "warning");
      }
      return null;
    }
  }, [applyView, setView, stillOn, t, toast]);

  /** Un rifiuto di un comando: la sessione cambiata altrove si rilegge e la barra la mostra com'è; gli altri si dicono. */
  const recover = useCallback(
    async (error: unknown) => {
      if (rereads(error)) {
        await reread();
        return;
      }
      setNotice(error);
    },
    [reread],
  );

  /** Un comando alla volta: spegne i comandi e ferma il conto alla rovescia finché il server non risponde. */
  const run = useCallback(
    async <T,>(work: () => Promise<T>, onError: (error: unknown) => Promise<void> = recover): Promise<T | null> => {
      if (busyRef.current) return null;
      busyRef.current = true;
      setBusy(true);
      setNotice(null);
      try {
        return await work();
      } catch (error) {
        await onError(error);
        return null;
      } finally {
        busyRef.current = false;
        setBusy(false);
      }
    },
    [recover],
  );

  /**
   * Il comando di «Lead successivo». Con `resumeFirst` (il clic di chi preme «Lead successivo», mai il conto che scade da solo) e la
   * sessione in pausa sul server, prima la riprende e poi avanza con la versione della vista ripresa, tutto in un `run()`: i
   * comandi restano spenti per tutti e due i passi. Il server rifiuta l'avanzamento di una sessione in pausa (`409
   * dialer_session_paused`), e il conto da solo non deve mai riprendere una pausa che qualcuno ha chiesto. Una ripresa rifiutata
   * segue `recover` come ogni comando e non avanza. Una pausa soltanto del browser (sessione attiva sul server) avanza come
   * sempre. Dopo la ripresa, un avanzamento rifiutato lascia il Lead aperto con la sessione attiva: anche la fase del browser
   * esce dalla pausa, come in `resume()`, così la barra non resta in «In pausa» su una sessione che il server dice attiva.
   */
  const advanceWith = useCallback(
    async (passOver: boolean, resumeFirst = false) => {
      const at = tabRef.current;
      const now = sessionRef.current;
      if (!at || !now) return;
      const requestId = newRequestId();
      const view = await run(async () => {
        let expectedVersion = now.version;
        if (resumeFirst && now.state === "paused") {
          const resumed = await requestDialerSwitch(at, now.id, "resume", { expectedVersion });
          applyView(resumed);
          if (resumed.state !== "active") return null;
          expectedVersion = resumed.version;
          dispatch({ type: "resume" });
        }
        for (let attempt = 0; ; attempt += 1) {
          try {
            return await requestDialerAdvance(at, now.id, {
              expectedVersion,
              passOver,
              requestId,
            });
          } catch (error) {
            if (!isCode(error, "network") || attempt >= NETWORK_RETRIES) throw error;
            await wait(TICK_MS);
          }
        }
      });
      if (!view || !stillOn(at)) return;
      applyView(view, { fresh: true });
      goTo(view);
    },
    [applyView, dispatch, goTo, run, stillOn],
  );

  useLayoutEffect(() => {
    autoAdvanceRef.current = () => {
      busyTimer.current = null;
      const now = sessionRef.current;
      if (now?.state === "active" && now.current === null) void advanceWith(false);
    };
  });

  /** La chiamata non è partita: il conto alla rovescia torna in pausa e la sessione si rilegge (il Lead può essere passato). */
  const callFailed = useCallback(() => {
    const now = sessionRef.current;
    if (!now) return;
    dispatch({ type: "call_failed", countdownSeconds: now.countdownSeconds });
    void reread();
  }, [dispatch, reread]);

  /** L'attesa dell'esito di Gioia365 per la chiamata `callId` finisce: la postazione chiede l'esito come sempre (TEL-32). */
  const release = useCallback((callId: string) => {
    releasedRef.current = callId;
    setReleased(callId);
  }, []);
  const chooseOutcome = useCallback((callId: string) => release(callId), [release]);

  /**
   * Dopo «Nessuna risposta» (di Gioia365, o scelta a mano) con la postazione chiusa e «Senza risposta, richiama da solo…» acceso
   * (TEL-32): la vista riletta decide. Con il tentativo della sequenza a metà lo stesso Lead si richiama dopo i secondi prima della
   * chiamata, come chiamata `made + 1` di `required`; senza secondi la chiamata partirebbe subito, e allora prima aspetta la scheda
   * del Lead nella pagina e la pagina libera, come il conto del richiamo che non c'è. Altrimenti parte il conto verso il Lead
   * successivo (TEL-27). Un altro Lead aperto intanto, una sessione non più viva o un comando di chi chiama nel frattempo
   * (`deciding` annullata) non ricevono niente. Una lettura fallita, o che torna dopo il limite (`DECISION_READ_MS`), non muove
   * niente (ruling 7 del controllore): il Lead resta «fatto», con «Lead successivo» acceso e principale; un conto verso il Lead
   * successivo partito da una vista vecchia chiuderebbe da solo il tentativo a metà (`passOver`) per un guasto della rete, e un
   * richiamo deciso minuti dopo farebbe partire una chiamata vera a sorpresa.
   */
  const afterNoAnswer = useCallback(
    async (key: string) => {
      const token = {};
      deciding.current = token;
      const still = () => deciding.current === token && machineRef.current?.key === key;
      const view = await answerWithin(reread(), DECISION_READ_MS);
      if (!still()) return;
      if (view === null) {
        deciding.current = null;
        return;
      }
      if (!isLive(view) || leadKey(view) !== key) return;
      const pending = view.attemptPending;
      if (view.autoRedial && pending !== null && pending.made < pending.required) {
        const event: DialerBrowserEvent = { type: "redial", countdownSeconds: view.countdownSeconds, call: pending.made + 1, of: pending.required };
        const open = machineRef.current;
        const offCard = () => {
          const shown = cardRef.current;
          const at = tabRef.current;
          return shown === null || at === null || shown.tab !== tabKey(at) || shown.leadId !== sessionRef.current?.current?.id;
        };
        if (open && reduceDialerState(open.state, event).phase === "calling" && !(await untilFree(() => offCard() || pageHoldsAdvance(), still))) return;
        deciding.current = null;
        dispatch(event);
        return;
      }
      deciding.current = null;
      dispatch({ type: "desk_closed", advanceSeconds: view.advanceSeconds });
    },
    [dispatch, reread],
  );

  /**
   * Una lettura dell'attesa dell'esito di Gioia365 per la chiamata `callId` (TEL-32), una alla volta. Con «Nessuna risposta» di
   * Gioia365 proprio su questa chiamata (l'ultima della sessione al Lead aperto: una lettura che dice ancora la chiamata di prima
   * non vale) la postazione lo prende come salvato e si chiude, anche con l'impostazione spenta intanto o la sessione non più viva:
   * l'esito è già sul server, e chiederlo di nuovo darebbe soltanto un esito arrivato tardi; il richiamo lo decide poi la chiusura,
   * soltanto con la sessione viva e l'impostazione accesa. Con la chiamata finita senza quell'esito (ha risposto qualcuno, chiusa
   * mentre squillava, mai arrivata al Lead), l'impostazione spenta, la sessione non più viva, o dopo 15 secondi di letture chieste
   * (anche se una lettura non torna mai), l'attesa finisce e la postazione chiede l'esito come sempre. Dopo «Scegli l'esito», o con
   * la postazione cambiata intanto, la lettura arrivata non prende niente.
   */
  const checkAutomatic = useCallback(
    async (callId: string) => {
      let count = readsRef.current;
      if (count?.callId !== callId) {
        count = { callId, asked: 0, reading: false };
        readsRef.current = count;
      }
      count.asked += 1;
      if (count.reading) {
        // La lettura di prima non è ancora tornata: l'attesa non va oltre il suo limite (ruling S3), e quella lettura, se torna,
        // non prende più niente.
        if (count.asked >= AUTOMATIC_OUTCOME_READS) release(callId);
        return;
      }
      count.reading = true;
      let view: DialerSessionView | null;
      try {
        view = await reread();
      } finally {
        count.reading = false;
      }
      if (releasedRef.current === callId || awaitingRef.current !== callId) return;
      const last = view?.currentCall ?? null;
      if (view !== null && last?.id === callId && last.outcome === "no_answer" && last.automatic) {
        automaticClose.current = callId;
        if (!callsRef.current.acceptAutomaticOutcome(callId)) automaticClose.current = null;
        return;
      }
      const settled = last?.id === callId && last.ended;
      if ((view !== null && (!isLive(view) || !view.autoRedial || settled)) || count.asked >= AUTOMATIC_OUTCOME_READS) release(callId);
    },
    [release, reread],
  );

  /** Fa partire la chiamata chiesta dal conto alla rovescia, appena la scheda del Lead aperto è nella pagina. */
  const dial = useCallback(() => {
    const at = tabRef.current;
    const now = sessionRef.current;
    const open = machineRef.current;
    const shown = cardRef.current;
    if (!at || !now?.current || !open?.dialPending || open.state.phase !== "calling") return;
    if (!shown || shown.tab !== tabKey(at) || shown.leadId !== now.current.id) return;
    commit({ ...open, dialPending: false });
    const phone = callsRef.current;
    const free = phone.session.phase === "idle" || phone.session.phase === "error";
    if (!free || shown.phone === null) {
      callFailed();
      return;
    }
    void phone.start({
      tab: at,
      leadId: shown.leadId,
      phone: shown.phone,
      leadName: shown.leadName,
      dialerSessionId: now.id,
    });
  }, [callFailed, commit]);

  // La chiamata chiesta parte appena può: subito, o quando la scheda del Lead arriva.
  useEffect(() => {
    dial();
  });

  // Le funzioni di adesso per gli effetti che devono ripartire soltanto quando cambia ciò che osservano.
  const actions = useRef({ callFailed, reread, advanceWith, afterNoAnswer, checkAutomatic });
  useLayoutEffect(() => {
    actions.current = { callFailed, reread, advanceWith, afterNoAnswer, checkAutomatic };
  });

  // Le fasi della chiamata di M4 sul Lead aperto portano la fase della cascata.
  const seen = useRef<{
    phase: CallSession["phase"] | "other";
    callId: string | null;
    /**
     * L'esito che chi chiama ha dato nella postazione, per sapere alla sua chiusura se era «Nessuna risposta» (TEL-32): quello
     * salvato, o quello in salvataggio. «Chiudi la chiamata» salva e chiude di seguito, e il disegno può passare da «in
     * salvataggio» a chiusa senza mostrare «salvato»; chiusa così, l'esito salvato è proprio quello mandato. Uno arrivato tardi
     * (`late`: dopo «Scegli l'esito» un esito diverso, e la chiamata aveva già «Nessuna risposta» di Gioia365) non chiude al primo
     * clic e la postazione lo mostra salvato prima di chiudersi: non conta, la scelta di chi chiama era un'altra, e alla chiusura
     * segue il conto verso il Lead successivo come per ogni postazione chiusa, mai il richiamo.
     */
    outcome: CallView["outcome"];
    /**
     * La chiamata è partita con questa sessione: soltanto dopo di lei la fila riparte da sola (TEL-32). Una chiamata ripresa con
     * «Manca l'esito…» (dopo un ricaricamento) non porta la sessione, e dopo di lei non si richiama.
     */
    sessionCall: boolean;
    /**
     * La postazione propone la diagnosi del numero di una chiamata che non ha contato (TEL-10) e l'esito non è salvato:
     * chiusa così («Controllo dopo»), la chiamata vale come una non arrivata al Lead (TEL-27, TEL-30, TEL-32): il conto alla
     * rovescia torna in pausa sullo stesso Lead, nessun passaggio al Lead successivo e nessun tentativo chiuso.
     */
    diagnosis: boolean;
  }>({ phase: "other", callId: null, outcome: null, sessionCall: false, diagnosis: false });
  const { session: call, stepsDone } = calls;
  const openLead = session?.current?.id ?? null;
  const deskBlocked = deskBlock(calls, scopeKey, openLead, browser?.phase ?? null);
  // La chiamata di cui la postazione aspetta l'esito di Gioia365, nello stesso disegno della fase dell'esito (TEL-32).
  const awaiting = awaitedCall(calls.session, session, scopeKey, machine, released);
  // Durante l'attesa «Interrompi» resta acceso (ruling S3: l'attesa non blocca): il clic la chiude e segue le regole di sempre.
  const interruptBlocked = awaiting !== null ? null : deskBlocked;
  useLayoutEffect(() => {
    awaitingRef.current = awaiting;
  });
  useEffect(() => {
    const before = seen.current;
    const about = scopeKey !== null && openLead !== null && callOfLead(call, scopeKey, openLead);
    const callId = about && call.phase !== "idle" ? (call.call?.id ?? null) : null;
    const save = about && call.phase === "outcome" ? call.save : null;
    const outcome = save?.state === "saved" ? (save.late ? null : save.call.outcome) : save?.state === "saving" ? save.input.outcome : null;
    const now = sessionRef.current;
    const sessionCall = about && call.phase !== "idle" && call.target !== null && now !== null && call.target.dialerSessionId === now.id;
    const diagnosis = about && call.phase === "outcome" && call.save.state !== "saved" && numberDiagnosisOf(call.call) !== null;
    seen.current = { phase: about ? call.phase : "other", callId, outcome, sessionCall, diagnosis };
    const open = machineRef.current;
    if (!open || openLead === null) return;
    if (!about) {
      // La chiamata chiusa prima che il telefono componesse (abbandonata): non è partita.
      if (before.phase === "starting" || before.phase === "ringing" || before.phase === "live") actions.current.callFailed();
      // La postazione chiusa dopo l'esito con «Chiudi la chiamata» (`close()` riesce soltanto con i passi obbligatori fatti), o con
      // l'esito di Gioia365: il Lead è fatto. Dopo «Nessuna risposta», con «Senza risposta, richiama da solo…» acceso e una chiamata
      // con la sessione di questa pagina, la vista riletta decide fra il richiamo e il conto verso il Lead successivo (TEL-32);
      // altrimenti parte il conto verso il successivo (decisione del 9 ottobre 2026). Chiusa da un comando già in corso («Lead
      // successivo», «Interrompi» chiudono la postazione e poi partono), il passo è quel comando: nessun conto, che potrebbe
      // avanzare una seconda volta. La chiusura con l'esito di Gioia365 non è mai di un comando (una «Pausa» in corso non la cambia).
      // «Controllo dopo» sulla diagnosi del numero, senza un comando in corso, è una chiamata non arrivata al Lead (TEL-10).
      else if (before.phase === "outcome" && before.diagnosis && !busyRef.current) actions.current.callFailed();
      else if (before.phase === "outcome") {
        const automatic = automaticClose.current !== null && automaticClose.current === before.callId;
        automaticClose.current = null;
        const commanded = !automatic && busyRef.current;
        if (!commanded && before.sessionCall && (automatic || before.outcome === "no_answer") && now?.autoRedial === true && !open.restored) {
          dispatch({ type: "desk_closed", advanceSeconds: null });
          void actions.current.afterNoAnswer(open.key);
        } else dispatch({ type: "desk_closed", advanceSeconds: commanded ? null : (now?.advanceSeconds ?? null) });
      }
      return;
    }
    switch (call.phase) {
      case "starting":
      case "ringing":
      case "live":
      case "ending":
        dispatch({ type: "call_started" });
        return;
      case "outcome":
        dispatch({ type: "call_started" });
        dispatch({ type: "call_ended" });
        if (stepsDone) dispatch({ type: "outcome_saved" });
        // Finita la chiamata la sessione si rilegge: i fatti e, da R2, il tentativo da finire. Senza risposta l'esito può darlo
        // Gioia365 (TEL-32): la postazione lo aspetta (`awaiting`, già nel disegno) con le letture qui sotto.
        if (before.phase !== "outcome" || before.callId !== callId) void actions.current.reread();
        return;
      case "error":
        actions.current.callFailed();
        return;
    }
  }, [call, stepsDone, openLead, scopeKey, dispatch]);

  // L'attesa dell'esito di Gioia365: una lettura al secondo, finché la postazione aspetta quella chiamata (TEL-32).
  useEffect(() => {
    if (awaiting === null) return;
    const timer = setInterval(() => void actions.current.checkAutomatic(awaiting), AUTOMATIC_OUTCOME_READ_MS);
    return () => clearInterval(timer);
  }, [awaiting]);

  // Un Lead che adesso si salta da solo (un collega lo ha preso intanto) si salta, anche dopo una chiamata non partita, con il conto
  // verso il Lead successivo; una sessione messa in pausa altrove si ferma anche qui.
  useEffect(() => {
    const open = machineRef.current;
    if (!open || !session?.current) return;
    if (session.current.passover !== null && open.state.phase !== "passed" && !(open.state.phase === "paused" && open.state.pausedFrom === "passed")) {
      let state = open.state;
      if (state.phase === "paused" && state.pausedFrom === "countdown" && session.state === "active") state = reduceDialerState(state, { type: "resume" });
      state = reduceDialerState(state, { type: "lead_passed", advanceSeconds: session.advanceSeconds });
      if (state !== open.state) commit({ ...open, state, dialPending: false });
    }
    if (session.state === "paused" && open.state.phase !== "paused") dispatch({ type: "pause" });
  }, [session, dispatch, commit]);

  // Il conto alla rovescia e quello verso il Lead successivo: un secondo alla volta, soltanto sulla scheda del Lead aperto, senza
  // finestre né comandi in corso, mai per la sessione ritrovata. Un timer solo, che riparte quando cambia il conto che scorre:
  // mai due `tick` nello stesso secondo, e il primo secondo del conto dopo la chiamata è intero. Il conto verso il Lead
  // successivo salta anche il secondo in cui sulla scheda c'è una finestra aperta o si scrive, o la scheda del browser è nascosta
  // (`pageHoldsAdvance`): i secondi restano, e riprende quando la finestra si chiude, il cursore esce dal campo o si torna sulla
  // scheda. Così anche il conto del richiamo dello stesso Lead (TEL-32), che altrimenti farebbe partire una chiamata senza nessuno
  // davanti allo schermo; il conto alla rovescia di un Lead appena aperto no.
  const onCard = card !== null && scopeKey !== null && card.tab === scopeKey && card.leadId === openLead;
  const running = machine?.restored === false && onCard && holds === 0 && !busy;
  const ticking = running && browser?.phase === "countdown";
  const advanceLeft = running && browser !== null && isAdvancing(browser) ? browser.advanceLeft : null;
  const counting = ticking && browser !== null ? (browser.redial !== null ? "redial" : "countdown") : advanceLeft !== null && advanceLeft > 0 ? "advance" : null;
  useEffect(() => {
    if (counting === null) return;
    const timer = setInterval(() => {
      if ((counting === "advance" || counting === "redial") && pageHoldsAdvance()) return;
      dispatch({ type: "tick" });
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [counting, key, dispatch]);

  // Allo zero il Lead successivo, con il comando di «Lead successivo» e una volta sola: il conto si toglie prima di partire, così
  // una risposta che non arriva o un rifiuto lasciano il Lead aperto con «Lead successivo» a mano. Con un tentativo della sequenza
  // a metà il passaggio lo chiude (`passOver`), come «Apri il Lead successivo» della finestra «Decidi tu»; il Lead che si salta
  // da solo no. Con una finestra aperta sulla scheda, un campo in cui si scrive o la scheda del browser nascosta (anche con
  // «subito») si aspetta, guardando a ogni secondo.
  const advanceNow = advanceLeft === 0;
  useEffect(() => {
    if (!advanceNow) return;
    const go = (): boolean => {
      if (pageHoldsAdvance()) return false;
      const open = machineRef.current;
      if (open && isAdvancing(open.state) && open.state.advanceLeft === 0) {
        const passOver = open.state.phase === "done" && sessionRef.current?.attemptPending != null;
        commit({ ...open, state: { ...open.state, advanceLeft: null } });
        void actions.current.advanceWith(passOver);
      }
      return true;
    };
    if (go()) return;
    const timer = setInterval(() => {
      if (go()) clearInterval(timer);
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [advanceNow, commit]);

  // Il battito, finché la sessione è viva; una sessione che il server ha chiuso intanto si rilegge (e mostra il riepilogo).
  const liveId = session && isLive(session) ? session.id : null;
  useEffect(() => {
    if (!tab || liveId === null) return;
    const beat = async () => {
      const now = sessionRef.current;
      if (!now || now.id !== liveId) return;
      try {
        const answer = await requestDialerHeartbeat(tab, liveId);
        if (answer.reservedUntil === null && now.current !== null) await actions.current.reread();
      } catch (error) {
        if (isCode(error, "invalid_state", "dialer_session_not_found")) await actions.current.reread();
      }
    };
    const timer = setInterval(() => void beat(), HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, [tab, liveId]);

  // Cambiare azienda, entrare nella «Guarda come…»: la sessione resta salvata sul server, e il guscio lo dice.
  // `tasks`: la sessione che resta salvata viene dai Task, e l'avviso dice «dai Task» e non «dalla lista» (questione 339).
  const lastScope = useRef<{ key: string | null; live: boolean; tasks: boolean }>({
    key: scopeKey,
    live: false,
    tasks: false,
  });
  const sessionLive = liveId !== null;
  const sessionFromTasks = session?.source.type === "task_view";
  useEffect(() => {
    const before = lastScope.current;
    if (before.key !== scopeKey) {
      if (before.key !== null && before.live) toast.show(t(before.tasks ? "dialer.leaveWithSessionTasks" : "dialer.leaveWithSession"), "accent");
      setSummary(null);
      setNotice(null);
    }
    lastScope.current = { key: scopeKey, live: sessionLive, tasks: sessionFromTasks };
  }, [scopeKey, sessionLive, sessionFromTasks, t, toast]);

  useEffect(
    () => () => {
      if (busyTimer.current) clearTimeout(busyTimer.current);
      // Una decisione dopo «Nessuna risposta» ancora in corso non fa più niente, e una lettura dell'attesa che torna dopo non prende
      // l'esito di Gioia365 (TEL-32).
      deciding.current = null;
      awaitingRef.current = null;
    },
    [],
  );

  const queueQuery = useQuery({
    ...dialerQueueQuery(queueOpen ? tab : null, session?.id ?? null),
  });

  const start = useCallback(
    async (input: { tab: TabContext; source: DialerSource; mode: "start" | "resume" | "restart" }) => {
      let view: DialerSessionView | null = null;
      for (let attempt = 0; view === null; attempt += 1) {
        try {
          view = await requestDialerStart(input.tab, {
            source: input.source,
            mode: input.mode,
          });
        } catch (error) {
          const pause = START_RETRY_MS[attempt];
          if (!isCode(error, "dialer_queue_busy") || pause === undefined) throw error;
          await wait(pause);
        }
      }
      setSummary(null);
      setNotice(null);
      if (!tabRef.current || tabKey(tabRef.current) !== tabKey(input.tab)) return;
      busyTries.current = 0;
      applyView(view, { fresh: true });
      goTo(view);
    },
    [applyView, goTo],
  );

  const callNow = useCallback(() => dispatch({ type: "call_now" }), [dispatch]);
  const stay = useCallback(() => dispatch({ type: "stay" }), [dispatch]);

  const pause = useCallback(async () => {
    const at = tabRef.current;
    const now = sessionRef.current;
    if (!at || !now) return;
    // Il conto alla rovescia si ferma subito, prima della risposta del server.
    dispatch({ type: "pause" });
    const view = await run(() =>
      requestDialerSwitch(at, now.id, "pause", {
        expectedVersion: now.version,
      }),
    );
    if (view && stillOn(at)) applyView(view);
  }, [applyView, dispatch, run, stillOn]);

  const resume = useCallback(async () => {
    const at = tabRef.current;
    const now = sessionRef.current;
    if (!at || !now) return;
    // Una pausa soltanto del browser (una chiamata non partita) riprende senza comandi.
    if (now.state === "paused") {
      const view = await run(() =>
        requestDialerSwitch(at, now.id, "resume", {
          expectedVersion: now.version,
        }),
      );
      if (!view || !stillOn(at)) return;
      applyView(view);
      if (view.state !== "active") return;
    }
    dispatch({ type: "resume" });
  }, [applyView, dispatch, run, stillOn]);

  const skip = useCallback(
    async (input: { reason: DialerSkipReason; note: string | null }): Promise<boolean> => {
      const at = tabRef.current;
      const now = sessionRef.current;
      if (!at || !now) return true;
      const leaving = now.current?.id ?? null;
      // Un rifiuto detto lascia il Lead da saltare: la finestra resta aperta e il conto alla rovescia non lo chiama.
      const said = { refused: false };
      // Il salto non ha una chiave: un guasto della rete non si ripete alla cieca, si rilegge la sessione.
      const view = await run(
        () =>
          requestDialerSkip(at, now.id, {
            expectedVersion: now.version,
            reason: input.reason,
            note: input.note,
            // Come di partenza sul server: il salto lascia aperto il tentativo a metà della sequenza (D9).
            passOver: false,
          }),
        async (error) => {
          if (!isCode(error, "network")) {
            said.refused = !rereads(error);
            return recover(error);
          }
          const fresher = await reread();
          if (!stillOn(at)) return;
          if (fresher?.current && fresher.current.id !== leaving) goTo(fresher);
          else {
            setNotice(error);
            said.refused = true;
          }
        },
      );
      if (!view) return !said.refused;
      if (!stillOn(at)) return true;
      applyView(view, { fresh: true });
      goTo(view);
      return true;
    },
    [applyView, goTo, recover, reread, run, stillOn],
  );

  const next = useCallback(
    async (input: { passOver: boolean }) => {
      const now = sessionRef.current;
      if (!now) return;
      // Premuto durante il conto verso il Lead successivo, il passo è questo: il conto si toglie, così un rifiuto lascia il Lead
      // aperto, a mano, e non lo fa avanzare da solo dopo.
      dispatch({ type: "stay" });
      // La postazione con l'esito salvato e i passi fatti si chiude: altrimenti non si va avanti (ruling Q8).
      const phone = callsRef.current;
      if (scopeKey !== null && now.current && phone.session.phase === "outcome" && callOfLead(phone.session, scopeKey, now.current.id) && !phone.close())
        return;
      // In pausa su un Lead già fatto (o che si salta da solo) il passo riprende la sessione e avanza (product owner, 9 ottobre 2026).
      await advanceWith(input.passOver, true);
    },
    [advanceWith, dispatch, scopeKey],
  );

  const interrupt = useCallback(async (): Promise<DialerSummary> => {
    const at = tabRef.current;
    const now = sessionRef.current;
    if (!at || !now) throw new Error("No dialer session");
    // Chi ferma la sessione durante il conto verso il Lead successivo non vuole il Lead dopo: il conto si toglie prima, così
    // un «Interrompi» rifiutato o senza rete non lascia partire il passaggio (e la chiamata al Lead dopo).
    dispatch({ type: "stay" });
    // Durante l'attesa dell'esito di Gioia365 il clic la chiude, come «Scegli l'esito» (TEL-32): l'esito di Gioia365 non si prende
    // più, e valgono le regole qui sotto (con l'esito ancora da dare, la postazione lo chiede e la sessione non si ferma).
    const waiting = awaitingRef.current;
    if (waiting !== null) release(waiting);
    // Come «Lead successivo» (ruling Q46): mai durante la chiamata né prima dell'esito e dei passi obbligatori; la postazione
    // con i passi fatti si chiude prima di fermare la sessione, così «Continua a chiamare» trova il telefono libero.
    const phone = callsRef.current;
    const leadId = now.current?.id ?? null;
    if (deskBlock(phone, scopeKey, leadId, machineRef.current?.state.phase ?? null) !== null) throw new Error("The open lead's call is not finished");
    if (scopeKey !== null && leadId !== null && phone.session.phase === "outcome" && callOfLead(phone.session, scopeKey, leadId) && !phone.close())
      throw new Error("The open lead's call desk did not close");
    busyRef.current = true;
    setBusy(true);
    setNotice(null);
    try {
      let shown: DialerSummary;
      try {
        shown = await requestDialerInterrupt(at, now.id, {
          expectedVersion: now.version,
        });
      } catch (error) {
        // Una sessione si ferma sempre: cambiata altrove, con la versione di adesso.
        if (!isCode(error, "version_conflict")) throw error;
        const fresher = await requestDialerSession(at, now.id);
        shown = await requestDialerInterrupt(at, now.id, {
          expectedVersion: fresher.version,
        });
      }
      setSummary({
        summary: shown,
        source: now.source,
        sourceName: now.sourceName,
        tab: at,
      });
      setView(null);
      return shown;
    } catch (error) {
      setNotice(error);
      throw error;
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [dispatch, release, scopeKey, setView]);

  const queue = useCallback(() => queueQuery, [queueQuery]);
  const closeSummary = useCallback(() => setSummary(null), []);
  const hold = useCallback(() => {
    setHolds((count) => count + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      setHolds((count) => count - 1);
    };
  }, []);
  const watchQueue = useCallback((open: boolean) => setQueueOpen(open), []);
  const registerCard = useCallback((target: DialerCardTarget) => {
    setCard(target);
    return () => setCard((shown) => (shown === target ? null : shown));
  }, []);

  const restored = machine?.restored ?? false;
  return useMemo(
    () => ({
      session,
      browser,
      start,
      callNow,
      pause,
      resume,
      stay,
      skip,
      next,
      interrupt,
      queue,
      tab,
      timeZone,
      restored,
      busy,
      interruptBlocked,
      awaitingOutcome: awaiting,
      chooseOutcome,
      notice,
      summary,
      closeSummary,
      hold,
      watchQueue,
      registerCard,
    }),
    [
      session,
      browser,
      start,
      callNow,
      pause,
      resume,
      stay,
      skip,
      next,
      interrupt,
      queue,
      tab,
      timeZone,
      restored,
      busy,
      interruptBlocked,
      awaiting,
      chooseOutcome,
      notice,
      summary,
      closeSummary,
      hold,
      watchQueue,
      registerCard,
    ],
  );
}
